import { create } from 'zustand';

/**
 * Store globale per la gestione errori.
 * Permette di mostrare toast + dialog con dettagli tecnici.
 */
export const useErrorStore = create((set, get) => ({
  // Ultimo errore per il dialog dettagliato
  lastError: null,
  showErrorDialog: false,
  
  // Imposta errore e mostra dialog
  setError: (error) => set({ 
    lastError: {
      ...error,
      timestamp: new Date().toISOString()
    }, 
    showErrorDialog: true 
  }),
  
  // Chiudi dialog
  closeErrorDialog: () => set({ showErrorDialog: false }),
  
  // Pulisci errore
  clearError: () => set({ lastError: null, showErrorDialog: false })
}));

/**
 * Formatta un errore da Axios o generico in un oggetto strutturato
 */
export function formatError(error, context = '') {
  // Errore Axios
  if (error.response) {
    const status = error.response.status;
    const data = error.response.data;
    
    // Gestisce errori di validazione Pydantic (422) con array di errori
    let message = 'Errore sconosciuto';
    if (data?.detail) {
      if (Array.isArray(data.detail)) {
        // Pydantic validation errors - estrai i messaggi
        message = data.detail.map(err => err.msg || JSON.stringify(err)).join('; ');
      } else if (typeof data.detail === 'string') {
        message = data.detail;
      } else {
        message = JSON.stringify(data.detail);
      }
    } else if (data?.message) {
      message = data.message;
    } else if (error.message) {
      message = error.message;
    }
    
    return {
      type: 'API_ERROR',
      code: `HTTP ${status}`,
      title: getTitleForStatus(status),
      message,
      context,
      technical: {
        url: error.config?.url,
        method: error.config?.method?.toUpperCase(),
        status,
        statusText: error.response.statusText,
        responseData: typeof data === 'object' ? JSON.stringify(data, null, 2) : data
      }
    };
  }
  
  // Errore di rete
  if (error.request) {
    return {
      type: 'NETWORK_ERROR',
      code: 'NETWORK',
      title: 'Errore di Connessione',
      message: 'Impossibile contattare il server. Verifica la connessione internet.',
      context,
      technical: {
        url: error.config?.url,
        method: error.config?.method?.toUpperCase(),
        errorMessage: error.message
      }
    };
  }
  
  // Errore generico JS
  return {
    type: 'CLIENT_ERROR',
    code: 'ERROR',
    title: 'Errore Applicazione',
    message: error.message || String(error),
    context,
    technical: {
      stack: error.stack,
      name: error.name
    }
  };
}

/**
 * Titolo user-friendly per codice HTTP
 */
function getTitleForStatus(status) {
  const titles = {
    400: 'Richiesta non valida',
    401: 'Sessione scaduta',
    403: 'Accesso negato',
    404: 'Risorsa non trovata',
    409: 'Conflitto dati',
    413: 'File troppo grande',
    422: 'Dati non validi',
    429: 'Troppe richieste',
    500: 'Errore del server',
    502: 'Server non raggiungibile',
    503: 'Servizio non disponibile',
    504: 'Timeout del server'
  };
  return titles[status] || `Errore ${status}`;
}

/**
 * Suggerimento azione per l'utente
 */
export function getActionSuggestion(errorType, code) {
  if (errorType === 'NETWORK_ERROR') {
    return 'Controlla la connessione internet e riprova.';
  }
  
  const statusCode = parseInt(code?.replace('HTTP ', ''));
  
  const suggestions = {
    401: 'Effettua nuovamente il login.',
    403: 'Non hai i permessi necessari per questa operazione.',
    404: 'Il contenuto richiesto non esiste più.',
    413: 'Riduci la dimensione del file e riprova.',
    429: 'Attendi qualche minuto prima di riprovare.',
    500: 'Riprova tra qualche istante. Se il problema persiste, contatta il supporto.',
    502: 'Il server è temporaneamente non raggiungibile. Riprova tra poco.',
    503: 'Il servizio è in manutenzione. Riprova più tardi.',
    504: 'La richiesta ha impiegato troppo tempo. Riprova con file più piccoli.'
  };
  
  return suggestions[statusCode] || 'Riprova l\'operazione o contatta il supporto.';
}
